import { inject } from '@angular/core';
import {
  Routes,
  Route,
  UrlSegment,
  CanMatchFn,
  PartialMatchRouteSnapshot,
} from '@angular/router';
import { NotFoundComponent } from '@shared/not-found/not-found.component';
import { Store } from '@ngrx/store';
import { filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { selectProjectListingsAreLoaded } from '@core/state/ui/project-index/project-index-ui.selectors';
import { selectEntityMap } from '@core/state/entities/entity.selectors';
import { projectCanMatchGuard } from '@core/services/project/project-guard.service';
import { authGuardWrapper } from '@core/auth/auth-guard-wrapper.service';
import { AUTH_PROVIDER } from '@core/auth/auth-provider.token';
import { ProjectIndexComponent } from '@app/project-index/project-index.component';
export const authCanMatch: CanMatchFn = (
  route: Route,
  segments: UrlSegment[],
  _currentSnapshot: PartialMatchRouteSnapshot
) => {
  const authProvider = inject(AUTH_PROVIDER);
  // Wait for isLoading$ to settle to false before evaluating isAuthenticated$.
  // Every IAuthProvider implementation starts isLoading$ true (or, for
  // BffAuthProvider, derives isAuthenticated$ from a BehaviorSubject(null)
  // while checkSession() is still in flight) — evaluating isAuthenticated$
  // before that settles observes a synchronous "not yet known" false and
  // redirects even when a valid session exists. Mirrors the isLoading$ gate
  // already used by BffAuthProvider.canActivate (see bff-auth.provider.ts).
  return authProvider.isLoading$.pipe(
    filter((loading) => !loading),
    take(1),
    switchMap(() =>
      authProvider.isAuthenticated$.pipe(
        tap((loggedIn) => {
          if (!loggedIn) {
            authProvider.loginWithRedirect({
              appState: { target: segments.join('/') },
            });
          }
        }),
        filter((loggedIn) => loggedIn)
      )
    )
  );
};

export const projectCheckMatch: CanMatchFn = (
  route: Route,
  segments: UrlSegment[],
  _currentSnapshot: PartialMatchRouteSnapshot
) => {
  const store = inject(Store);
  const projectId = segments[0].path;
  return store.select(selectEntityMap('projects')).pipe(
    withLatestFrom(store.select(selectProjectListingsAreLoaded)),
    filter(([_, plLoaded]) => plLoaded),
    map(([projectMap, _]) => {
      console.log('checking route!');
      const allow = projectMap?.[projectId]?.isMember ?? false;
      console.log('is route allowed?', allow);
      return allow;
    }),
    take(1)
  );
};
export const appRoutes: Routes = [
  {
    // No identity in the route: current review authority arrives only from its opener.
    path: 'study-source-window',
    loadComponent: () =>
      import('./study-source-window/study-source-window.component').then(
        (m) => m.StudySourceWindowComponent
      ),
  },
  {
    // This route contains no study identity or PDF URL. It can render only
    // after the exact-origin opener handshake in StudyPdfViewerComponent.
    path: 'pdf-viewer',
    loadComponent: () =>
      import('./pdf-viewer/study-pdf-viewer.component').then(
        (m) => m.StudyPdfViewerComponent
      ),
  },
  {
    path: 'home',
    redirectTo: '',
  },
  {
    path: '',
    loadChildren: () => import('./info/info.routes').then((m) => m.infoRoutes),
  },
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.routes').then((m) => m.authRoutes),
  },
  {
    path: 'invitations/:invitationToken',
    component: ProjectIndexComponent,
    canMatch: [
      authCanMatch,
      (route: Route, segments: UrlSegment[]) => {
        if (!segments[1]) {
          return false;
        }
      },
    ],
  },
  {
    path: 'projects',
    canMatch: [authCanMatch],
    canActivateChild: [authGuardWrapper],
    children: [
      {
        path: ':projectId',
        canMatch: [projectCanMatchGuard],
        loadChildren: () =>
          import('./project/project.routes').then((m) => m.projectRoutes),
      },
      {
        path: '',
        loadChildren: () =>
          import('./project-index/project-index.routes').then(
            (m) => m.projectIndexRoutes
          ),
      },
    ],
  },
  {
    path: 'admin',
    loadChildren: () =>
      import('./admin/admin.routes').then((m) => m.adminRoutes),
  },
  {
    path: 'manage',
    loadChildren: () =>
      import('./manage/manage.routes').then((m) => m.manageRoutes),
  },
  {
    path: 'about',
    loadChildren: () =>
      import('./about/about.routes').then((m) => m.aboutRoutes),
  },
  {
    path: 'not-found',
    component: NotFoundComponent,
    title: 'Page not found | SyRF',
  },
  {
    path: '**',
    redirectTo: 'not-found',
  },
];
